import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BillDetails, Card } from 'components'
import { fetchBankSlips, fetchInvoices, fetchPaidInvoices } from 'slices/financial'
import {
  DOWNLOAD_PAID_BILL_MESSAGE,
  FIREBASE_EVENTS,
  MODAL_KEYS,
  MY_BILLS_PAGE_TAB_LABEL,
  PLAN_STATUS_KEY,
} from 'app-constants'
import { STATICS } from 'statics'
import { setModal } from 'slices/modal'
import { useToaster } from 'hooks'
import { financialService } from 'services'
import { BillsCard } from '../bills-card'
import { BillsLine } from '../bills-line'
import './style.scss'

export function Bills({ contractId, selectedContract }) {
  const { invoices, bankSlips, paid } = useSelector(state => state.financial)
  const dispatch = useDispatch()
  const { showErrorToast } = useToaster()
  const { getPaidBillDownloadLink } = financialService()
  const isCanceledContract = selectedContract.contractStatus === PLAN_STATUS_KEY.ST_CONT_CANCELADO

  useEffect(() => {
    function getInvoices() {
      const isInvoiceInitialPage = invoices.page === 0

      if (isInvoiceInitialPage) dispatch(fetchInvoices(contractId))
    }

    getInvoices()
    dispatch(fetchBankSlips(contractId))
    dispatch(fetchPaidInvoices(contractId))
  }, [dispatch, contractId, invoices])

  function handleUnderstandBillsClick() {
    dispatch(setModal({ key: MODAL_KEYS.UNDERSTAND_YOUR_BILLS }))
  }

  function getResolvePendingBillsEvent() {
    return isCanceledContract
      ? FIREBASE_EVENTS.CLICK_SEE_BILLETS
      : FIREBASE_EVENTS.CLICK_ON_CANCELED_CONTRACT_ACCESS_MY_BILLS
  }

  function renderPending() {
    const { isLoading, content, error } = bankSlips
    return (
      <BillsCard
        title='Faturas abertas'
        eventClick={getResolvePendingBillsEvent()}
        tabId={MY_BILLS_PAGE_TAB_LABEL.BANKSLIPS}
        isLoading={isLoading}
        error={error}
        showTotal
        content={content}
        emptyMessage='Sua fatura será exibida aqui em breve'
      >
        <BillDetails selectedContractId={contractId} bankSlips={bankSlips} />
      </BillsCard>
    )
  }

  function renderInvoices(content) {
    return content
      .slice(0, 2)
      .map(({ date, id, urlFile, value }) => (
        <BillsLine
          key={id}
          dueDate={date}
          value={value}
          tagText='emitida'
          tagColor='active'
          downloadTooltip='Baixar nota fiscal.'
          validateDownload={() => !!urlFile}
          onDownload={() => window.open(urlFile)}
        />
      ))
  }

  function renderInvoicesCard() {
    const { error, content, isLoading } = invoices

    return (
      <BillsCard
        title='Notas fiscais'
        eventClick={FIREBASE_EVENTS.CLICK_ON_CANCELED_CONTRACT_INVOICES}
        tabId={MY_BILLS_PAGE_TAB_LABEL.INVOICES}
        isLoading={isLoading}
        error={error}
        content={content}
        isGrid
        emptyMessage='Não existem notas fiscais para esse contrato'
      >
        {renderInvoices(content)}
      </BillsCard>
    )
  }

  function renderPaidBillsCard() {
    const { error, content, isLoading } = paid

    async function handleDownload(id) {
      try {
        const { data } = await getPaidBillDownloadLink(id)

        if (data.url) window.open(data.url)
        else showErrorToast(DOWNLOAD_PAID_BILL_MESSAGE)
      } catch (err) {
        console.error(err)

        showErrorToast(DOWNLOAD_PAID_BILL_MESSAGE)
      }
    }

    function renderPaidBills() {
      return content
        .slice(0, 2)
        .map(({ dueDate, id, value }) => (
          <BillsLine
            key={id}
            dueDate={dueDate}
            value={value}
            tagText='Pago'
            tagColor='success'
            downloadTooltip='Baixar boleto pago'
            onDownload={() => handleDownload(id)}
          />
        ))
    }

    return (
      <BillsCard
        title='Histórico de pagamentos'
        eventClick={FIREBASE_EVENTS.CLICK_ON_CANCELED_CONTRACT_PAYMENT_HISTORY}
        tabId={MY_BILLS_PAGE_TAB_LABEL.PAID}
        isLoading={isLoading}
        error={error}
        content={content}
        isGrid
        emptyMessage='Não existem pagamentos para esse contrato'
      >
        {renderPaidBills()}
      </BillsCard>
    )
  }

  return (
    <Card className='bills-dashboard'>
      <div className='header'>
        <h3 className='title'>Minhas faturas</h3>
        <button
          type='button'
          className='question'
          data-tip='Entenda suas faturas'
          onClick={handleUnderstandBillsClick}
        >
          <STATICS.DENSE_QUESTION />
        </button>
      </div>
      <div className='content'>
        {renderPending()}
        {renderInvoicesCard()}
        {renderPaidBillsCard()}
      </div>
    </Card>
  )
}
